import styled, { css, useTheme } from "styled-components";
import {
  GroupType,
  SignalsGroupsSectionProps,
} from "@shared/features/onboarding/types";
import Title from "common/Typography/Title";
import React, {
  CSSProperties,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import VirtualCardsList from "common/Lists/VirtualCardsList";
import NoExternalAccount from "features/onboarding/methodSettings/signalsGroup/myGroupsMessages/NoExternalAccount";
import NoGroupsFound from "features/onboarding/methodSettings/signalsGroup/myGroupsMessages/NoGroupsFound";
import Flex from "common/Boxes/Flex";
import useSignalsGroupsSection from "@shared/features/onboarding/hooks/useSignalsGroupsSection";
import {
  MOBILE_ONBOARDING_GROUP_CARD_HEIGHT,
  MOBILE_ONBOARDING_GROUP_CARD_WIDTH,
  ONBOARDING_GROUP_CARD_HEIGHT,
  ONBOARDING_GROUP_CARD_WIDTH,
  ONBOARDING_MY_GROUP_CARD_HEIGHT,
} from "@shared/features/onboarding/constants";
import useGetVirtualListCardDimensions from "utils/hooks/useGetVirtualListCardDimensions";
import ScoreModal from "features/newMarketplace/common/ScoreModal";
import EqualColumnsGrid from "common/Grids/EqualColumnsGrid";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import useNumCardsInRow from "utils/hooks/useNumCardsInRow";
import GroupPageEmptyState from "features/onboarding/methodSettings/signalsGroup/myGroupsMessages/GroupPageEmptyState";
import EmptyView from "common/Empty/EmptyView";
import { EmptyStatesType } from "common/Icons/types.ts";
import { useBottomNavigator } from "utils/hooks/misc.tsx";
import FlatList from "common/Lists/FlatList.tsx";
import GroupCardComponent from "features/onboarding/methodSettings/signalsGroup/groupCards/GroupCardComponent.tsx";
import { PageTabsContext } from "common/Tabs/PageTabs.context.ts";
import useSearchInput from "utils/hooks/useSearchInput.tsx";
import { PageWrapperContext } from "common/Home/PageWrapper/PageWrapper.context.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import { HIDE_INACTIVE_GROUPS_CLASS, IS_WHOP } from "common/constants.ts";
import Switch from "common/Buttons/Switch.tsx";
import Text from "common/Typography/Text.tsx";

const SignalsGroupsSection = ({
  groupType,
  groups,
  isLoading,
  isFetching,
  isSuccess,
  titleStyle,
  extraButton,
  showSearch,
  style,
  hideTitle,
}: SignalsGroupsSectionProps & {
  style?: CSSProperties;
  /** When tabs already name the section (onboarding). */
  hideTitle?: boolean;
}) => {
  const theme = useTheme();
  const { t: tCommon } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const { t } = useTypedTranslation(LanguageNameSpaces.GROUPS);
  const { search: mobileSearch, useMobileSearch } =
    useContext(PageWrapperContext)!;
  const { setTabCount } = useContext(PageTabsContext) || {};
  const numCardsInRow = useNumCardsInRow(undefined, [750, 1200, 1500]);
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();

  const {
    filteredGroups,
    noExternalAccountConnected,
    noGroupsFound,
    isMyGroups,
    isPersonalGroups,
    scoreModalProps,
    setScoreModalGroup,
    showEmptyState,
    isGroupsPage,
    emptyStateTitle,
    refetch,
    searchValue,
    setSearchValue,
    searchValueToUse,
  } = useSignalsGroupsSection(
    groupType,
    groups,
    isSuccess,
    useMobileSearch && mobileSearch.show ? mobileSearch.value : undefined
  );
  const showHideInactiveToggle = isGroupsPage && isMyGroups && !IS_WHOP;
  const [hideInactive, setHideInactive] = useState(
    showHideInactiveToggle &&
      localStorage?.getItem(HIDE_INACTIVE_GROUPS_CLASS) === "true"
  );

  const finalFilteredGroups = useMemo(() => {
    if (!hideInactive) return filteredGroups;
    return filteredGroups?.filter(
      (group) => "is_active" in group && group.is_active && group.allow_follow
    );
  }, [filteredGroups, hideInactive]);

  const onHideInactiveClick = () => {
    const updatedValue = !hideInactive;
    localStorage?.setItem(
      HIDE_INACTIVE_GROUPS_CLASS,
      updatedValue ? "true" : "false"
    );
    setHideInactive(updatedValue);
  };

  const [SearchInput] = useSearchInput({
    placeholder: tCommon("labels.search_placeholder"),
    focusOnMount: false,
    onChange: setSearchValue,
    value: searchValue,
    style: { width: 215 },
  });

  const { cardHeight, cardWidth } = useGetVirtualListCardDimensions(
    isMyGroups
      ? ONBOARDING_MY_GROUP_CARD_HEIGHT
      : theme.isNewLook
      ? NEW_LOOK_GROUP_CARD_HEIGHT
      : ONBOARDING_GROUP_CARD_HEIGHT,
    isMyGroups
      ? ONBOARDING_MY_GROUP_CARD_HEIGHT
      : theme.isNewLook
      ? NEW_LOOK_MOBILE_GROUP_CARD_HEIGHT
      : MOBILE_ONBOARDING_GROUP_CARD_HEIGHT,
    ONBOARDING_GROUP_CARD_WIDTH,
    MOBILE_ONBOARDING_GROUP_CARD_WIDTH
  );

  useEffect(() => {
    setTabCount?.(groupType, isLoading ? undefined : groups?.length);
  }, [groupType, isLoading, groups]);

  const getHideInactiveToggle = () => {
    if (!showHideInactiveToggle) return;
    return (
      <Flex
        onClick={onHideInactiveClick}
        style={{ marginRight: 12 }}
        $justifyContent="flex-end"
      >
        <Switch
          checked={hideInactive}
          onClick={onHideInactiveClick}
          style={{ marginRight: 8 }}
        />
        <Text>
          {isBottomNavigator
            ? t("buttons.hide_inactive")
            : t("buttons.hide_inactive_groups")}
        </Text>
      </Flex>
    );
  };

  const getCardsListTitle = () => {
    // No title and nothing beside it: no row at all.
    if (hideTitle && !extraButton && !showSearch) return undefined;
    if (isGroupsPage && isBottomNavigator) {
      return (
        <Flex
          style={
            extraButton || showHideInactiveToggle
              ? { marginBottom: 8 }
              : undefined
          }
        >
          {getHideInactiveToggle() ?? <div />}
          {extraButton ? (
            <Flex $justifyContent="flex-end">{extraButton}</Flex>
          ) : undefined}
        </Flex>
      );
    }
    return (
      <div style={isGroupsPage ? { margin: "16px 0" } : undefined}>
        <Flex $justifyContent="space-between">
          {hideTitle ? (
            <div />
          ) : (
            <SectionTitle style={titleStyle}>{t(groupType)}</SectionTitle>
          )}
          <Flex>
            {numCardsInRow > 1 && getHideInactiveToggle()}
            {showSearch && !useMobileSearch && SearchInput}
            {extraButton}
          </Flex>
        </Flex>
        {numCardsInRow === 1 && (
          <div style={{ marginTop: 16 }}>{getHideInactiveToggle()}</div>
        )}
      </div>
    );
  };

  const getEmptyStateMessage = () => {
    let content: ReactNode;
    if (noExternalAccountConnected) content = <NoExternalAccount />;
    else if (noGroupsFound) content = <NoGroupsFound />;
    return <EmptyWrapper>{content}</EmptyWrapper>;
  };

  const myGroupsEmptyState = isGroupsPage ? (
    <GroupPageEmptyState
      noExternalAccountConnected={noExternalAccountConnected}
      noGroupsFound={noGroupsFound}
      searchValue={searchValueToUse}
    />
  ) : (
    getEmptyStateMessage()
  );

  const emptyView = (
    <EmptyView
      iconKey={
        isPersonalGroups ? EmptyStatesType.Users : EmptyStatesType.Marketplace
      }
      iconWidth={isBottomNavigator ? 100 : 140}
      titleStyle={{ maxWidth: 350 }}
      title={emptyStateTitle}
      isSearch={!!searchValueToUse?.length}
      hasData={!!groups?.length}
    />
  );

  const emptyState = (
    <>
      {!isGroupsPage && (
        <EmptyStateTitle $isGroupsPage={isGroupsPage}>
          {getCardsListTitle()}
        </EmptyStateTitle>
      )}
      <Flex $justifyContent="center">
        {isMyGroups ? myGroupsEmptyState : emptyView}
      </Flex>
    </>
  );

  const renderContent = ({
    item,
    index,
  }: {
    item: GroupType;
    index: number;
  }) => {
    return (
      <GroupCardComponent
        key={item.id}
        data={item}
        extraData={setScoreModalGroup}
        index={index}
      />
    );
  };

  const getGroupsPageList = () => {
    if (isBottomNavigator) {
      return (
        <FlatList
          data={finalFilteredGroups || []}
          loading={isLoading}
          renderContent={renderContent}
          onRefresh={isGroupsPage ? () => refetch(groupType) : undefined}
          renderTopContainer={isBottomNavigator ? getCardsListTitle : undefined}
          refreshing={isFetching}
          emptyState={emptyState}
          wrapperStyle={isBottomNavigator ? WRAPPER_STYLE : { padding: 0 }}
          paddingBottom={16}
        />
      );
    }
    return (
      <LoadingSpinnerOverlay
        isLoading={isLoading}
        backgroundColor={theme.colors.layoutBgColor}
        wrap={isLoading}
        hideContentWhileLoading={true}
        wrapStyle={isLoading ? { height: 200 } : undefined}
        spinnerSize="large"
      >
        <EqualColumnsGrid numColumns={numCardsInRow}>
          {finalFilteredGroups?.map((group, index) => {
            return (
              <GroupCardComponent
                key={group.id}
                data={group}
                extraData={setScoreModalGroup}
                index={index}
              />
            );
          })}
        </EqualColumnsGrid>
      </LoadingSpinnerOverlay>
    );
  };

  const getGroupsList = () => {
    if (isGroupsPage) {
      return getGroupsPageList();
    }
    return (
      <VirtualCardsList
        cardsData={finalFilteredGroups}
        extraCardsData={setScoreModalGroup}
        loading={isLoading}
        cardHeight={cardHeight}
        cardWidth={cardWidth}
        CardComponent={GroupCardComponent}
        title={getCardsListTitle()}
        peekNext={true}
      />
    );
  };

  return (
    <Wrapper style={style}>
      {isGroupsPage && !isBottomNavigator && getCardsListTitle()}
      {showEmptyState ? emptyState : getGroupsList()}
      <ScoreModal {...scoreModalProps} loading={isLoading} />
    </Wrapper>
  );
};

export default React.memo(SignalsGroupsSection);

/** The new-look card also carries the month chart. */
const NEW_LOOK_GROUP_CARD_HEIGHT = 340;
/** Narrow screens: the card's own height (a two-line name included), so no
    empty band shows under it. */
const NEW_LOOK_MOBILE_GROUP_CARD_HEIGHT = 262;
const WRAPPER_STYLE = { padding: 16 };

const Wrapper = styled.div`
  width: 100%;
`;
const EmptyWrapper = styled.div`
  padding: 0 20px;
`;
const titleStyle = css`
  font-weight: ${(p) => p.theme.typography.fontWeights.BOLD};
  font-size: ${(p) => p.theme.typography.fontSizes.L};
`;
const SectionTitle = styled(Title)`
  ${titleStyle}
`;
const EmptyStateTitle = styled(Title)<{ $isGroupsPage?: boolean }>`
  ${titleStyle}
  ${(p) =>
    !p.$isGroupsPage &&
    `
    margin: 0 0 16px 20px;
  `};
`;
